/**
 * TVG-142 / TVG-373: Geometry for the Table Plan canvas, at true size. The
 * canvas coordinate unit is **1 cm** (100 units = 1 metre), shared by the room
 * outlines and the furniture.
 *
 * A trestle is 180 × 90 cm and a chair 50 × 42 cm. A run is one or more
 * trestles joined end to end (`trestle_count`), a square is two trestles side by
 * side (180 × 180), and a top table is a run seated on one side only. Chairs
 * belong to a side of their table and spread evenly along it, so a run of three
 * trestles fits its 11 chairs per long side by bunching them up.
 *
 * A table's position is the top-left corner of its table top (not its chairs)
 * after rotation. Rotation is 0/90/180/270 clockwise about the table centre, so
 * every box stays axis aligned. Every table carries its own space: the table,
 * its chairs, and a walking space behind the chairs. Sides without chairs carry
 * no space, which is what lets tables snap together on those sides.
 *
 * Mirrors App\Support\TablePlanFurniture on the server; keep the two in sync.
 */

/** Pixels (canvas units) per metre. 1 unit = 1 cm. */
export const PX_PER_M = 100;

/** Visual grid square size in cm (0.5 m) — the on-canvas scale reference. */
export const GRID_STEP = 50;

/** Position snap granularity in cm (TVG-218). */
export const SNAP_STEP = 10;

export const TRESTLE_LONG = 180;
export const TRESTLE_DEEP = 90;

/** Chair width along the table edge, and its depth out from the edge. */
export const CHAIR_W = 50;
export const CHAIR_D = 42;

export const MAX_TRESTLES = 12;

export type Side = 'top' | 'right' | 'bottom' | 'left';

/** Clockwise: the order seat numbers run in. */
export const SIDES: ReadonlyArray<Side> = ['top', 'right', 'bottom', 'left'];

export type SideCounts = Record<Side, number>;

export type TileType =
  | 'long_12'
  | 'single_6'
  | 'single_3'
  | 'single_3_below'
  | 'single_3_vertical';

/**
 * The furniture offered in the Furniture panel. The legacy vertical top table
 * is still drawn but no longer offered: either top table can be turned.
 */
export const TILE_TYPES: ReadonlyArray<TileType> = ['single_6', 'long_12', 'single_3', 'single_3_below'];

export const TILE_LABEL: Record<TileType, string> = {
  single_6: 'Trestle',
  long_12: 'Square',
  single_3: 'Top table seats above',
  single_3_below: 'Top table seats below',
  single_3_vertical: 'Top table',
};

/** The two settings the furniture is drawn with. */
export interface FurnitureSettings {
  seatsPerSide: number;
  walkingSpace: number;
}

export const DEFAULT_FURNITURE: FurnitureSettings = { seatsPerSide: 3, walkingSpace: 60 };

export function emptyCounts(): SideCounts {
  return { top: 0, right: 0, bottom: 0, left: 0 };
}

export function isSquare(type: TileType): boolean {
  return type === 'long_12';
}

/** A top table is seated on one long side only. */
export function isTopTableType(type: TileType): boolean {
  return type === 'single_3' || type === 'single_3_below' || type === 'single_3_vertical';
}

export function isVertical(type: TileType): boolean {
  return type === 'single_3_vertical';
}

/** Runs can be lengthened and shortened; a square cannot. */
export function isRun(type: TileType): boolean {
  return !isSquare(type);
}

export function trestleCount(type: TileType, count: number | null | undefined): number {
  if (isSquare(type)) {
    return 1;
  }
  return Math.max(1, Math.min(MAX_TRESTLES, Math.round(count ?? 1)));
}

export interface Size {
  w: number;
  h: number;
}

/** Table top size at rotation 0. */
export function tableSize(type: TileType, trestles: number): Size {
  if (isSquare(type)) {
    return { w: TRESTLE_LONG, h: 2 * TRESTLE_DEEP };
  }
  const long = TRESTLE_LONG * trestleCount(type, trestles);
  return isVertical(type) ? { w: TRESTLE_DEEP, h: long } : { w: long, h: TRESTLE_DEEP };
}

export function swapsAxes(rotation: number): boolean {
  const r = normaliseRotation(rotation);
  return r === 90 || r === 270;
}

export function normaliseRotation(rotation: number): number {
  return ((rotation % 360) + 360) % 360;
}

export function rotatedTableSize(type: TileType, trestles: number, rotation: number): Size {
  const s = tableSize(type, trestles);
  return swapsAxes(rotation) ? { w: s.h, h: s.w } : s;
}

/**
 * The two long sides of a run: A runs with the clockwise order from the near
 * end to the far end, B runs back.
 */
export function runSides(type: TileType): { a: Side; b: Side; near: Side; far: Side } {
  return isVertical(type)
    ? { a: 'right', b: 'left', near: 'top', far: 'bottom' }
    : { a: 'top', b: 'bottom', near: 'left', far: 'right' };
}

export function seatedLongSides(type: TileType): Side[] {
  switch (type) {
    case 'long_12':
      return [...SIDES];
    case 'single_3':
      return ['top'];
    case 'single_3_below':
      return ['bottom'];
    case 'single_3_vertical':
      return ['left'];
    default:
      return ['top', 'bottom'];
  }
}

/** Sides a chair may go on. A top table offers one optional chair on each end. */
export function allowedSides(type: TileType): Side[] {
  if (!isTopTableType(type)) {
    return [...SIDES];
  }
  const sides = runSides(type);
  return SIDES.filter((s) => seatedLongSides(type).includes(s) || s === sides.near || s === sides.far);
}

export function sideMax(type: TileType, side: Side): number {
  if (isTopTableType(type)) {
    const sides = runSides(type);
    if (side === sides.near || side === sides.far) {
      return 1;
    }
  }
  return 40;
}

/**
 * Default chairs per side: perSide on each long side of every trestle plus one
 * per join, 1 on each open end; a square seats perSide on every side; a top
 * table seats its one long side and leaves its ends empty.
 */
export function defaultSideCounts(type: TileType, trestles: number, perSide: number): SideCounts {
  if (isSquare(type)) {
    return { top: perSide, right: perSide, bottom: perSide, left: perSide };
  }
  const counts = emptyCounts();
  const n = trestleCount(type, trestles);
  for (const side of seatedLongSides(type)) {
    counts[side] = n * perSide + (n - 1);
  }
  if (!isTopTableType(type)) {
    const sides = runSides(type);
    counts[sides.near] = 1;
    counts[sides.far] = 1;
  }
  return counts;
}

/** Spread a bare seat total over the seated long sides (old templates). */
export function distributeSeatCount(type: TileType, count: number): SideCounts {
  const counts = emptyCounts();
  const sides = seatedLongSides(type);
  for (let i = 0; i < Math.max(0, count); i++) {
    counts[sides[i % sides.length]]++;
  }
  return counts;
}

/** Where a rotated local side ends up in the room. */
export function worldSide(local: Side, rotation: number): Side {
  const steps = normaliseRotation(rotation) / 90;
  return SIDES[(SIDES.indexOf(local) + steps) % 4];
}

/** Room direction of a local axis after rotation. */
export function worldAxis(axis: 'x' | 'y', rotation: number): [number, number] {
  const r = normaliseRotation(rotation);
  const xs: Record<number, [number, number]> = { 0: [1, 0], 90: [0, 1], 180: [-1, 0], 270: [0, -1] };
  const ys: Record<number, [number, number]> = { 0: [0, 1], 90: [-1, 0], 180: [0, -1], 270: [1, 0] };
  return (axis === 'x' ? xs : ys)[r] ?? (axis === 'x' ? xs[0] : ys[0]);
}

export interface PlacedBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The table top as a box in the room. */
export function tableBox(type: TileType, trestles: number, x: number, y: number, rotation: number): PlacedBox {
  const s = rotatedTableSize(type, trestles, rotation);
  return { x1: x, y1: y, x2: x + s.w, y2: y + s.h };
}

/**
 * The table and its chairs: the table box grown by a chair's depth on every
 * side with chairs, plus `extra` (the walking space) on those same sides.
 */
export function footprint(
  type: TileType,
  trestles: number,
  x: number,
  y: number,
  rotation: number,
  counts: SideCounts,
  extra = 0,
): PlacedBox {
  const box = tableBox(type, trestles, x, y, rotation);
  const grow = CHAIR_D + extra;
  for (const side of SIDES) {
    if ((counts[side] ?? 0) <= 0) {
      continue;
    }
    switch (worldSide(side, rotation)) {
      case 'top':
        box.y1 -= grow;
        break;
      case 'right':
        box.x2 += grow;
        break;
      case 'bottom':
        box.y2 += grow;
        break;
      case 'left':
        box.x1 -= grow;
        break;
    }
  }
  return box;
}

export function boxesOverlap(a: PlacedBox, b: PlacedBox): boolean {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
}

/** Too close: either table's table-and-chairs reach into the other's space. */
export function tooClose(footA: PlacedBox, spaceA: PlacedBox, footB: PlacedBox, spaceB: PlacedBox): boolean {
  return boxesOverlap(footA, spaceB) || boxesOverlap(spaceA, footB);
}

export interface ChairAnchor {
  cx: number;
  cy: number;
  /** 'horizontal' = along a top/bottom side; 'vertical' = along left/right. */
  orientation: 'horizontal' | 'vertical';
}

/** Centre of chair `index` of `count` on a local side, in table-local space. */
export function localChairCentre(type: TileType, trestles: number, side: Side, index: number, count: number): ChairAnchor {
  const { w, h } = tableSize(type, trestles);
  const out = CHAIR_D / 2;
  const t = (index + 0.5) / Math.max(1, count);
  switch (side) {
    case 'top':
      return { cx: t * w, cy: -out, orientation: 'horizontal' };
    case 'right':
      return { cx: w + out, cy: t * h, orientation: 'vertical' };
    case 'bottom':
      return { cx: w - t * w, cy: h + out, orientation: 'horizontal' };
    default:
      return { cx: -out, cy: h - t * h, orientation: 'vertical' };
  }
}

/** Chair width along its side: full size when it fits, bunched up when not. */
export function chairWidth(type: TileType, trestles: number, side: Side, count: number): number {
  const { w, h } = tableSize(type, trestles);
  const length = side === 'top' || side === 'bottom' ? w : h;
  return Math.min(CHAIR_W, length / Math.max(1, count) - 2);
}

/** Chair rect in table-local space; a high chair draws a little smaller. */
export function chairRect(
  type: TileType,
  trestles: number,
  side: Side,
  index: number,
  count: number,
  highChair = false,
): { x: number; y: number; w: number; h: number; cx: number; cy: number; orientation: 'horizontal' | 'vertical' } {
  const a = localChairCentre(type, trestles, side, index, count);
  const along = chairWidth(type, trestles, side, count) * (highChair ? 0.8 : 1);
  const deep = CHAIR_D * (highChair ? 0.85 : 1);
  const w = a.orientation === 'horizontal' ? along : deep;
  const h = a.orientation === 'horizontal' ? deep : along;
  return { x: a.cx - w / 2, y: a.cy - h / 2, w, h, cx: a.cx, cy: a.cy, orientation: a.orientation };
}

/** Trestle rects in table-local space: one per trestle, two for a square. */
export function trestleRects(type: TileType, trestles: number): Array<{ x: number; y: number; w: number; h: number }> {
  const { w, h } = tableSize(type, trestles);
  if (isSquare(type)) {
    return [
      { x: 0, y: 0, w, h: h / 2 },
      { x: 0, y: h / 2, w, h: h / 2 },
    ];
  }
  const n = trestleCount(type, trestles);
  return Array.from({ length: n }, (_, i) =>
    isVertical(type)
      ? { x: 0, y: (i * h) / n, w, h: h / n }
      : { x: (i * w) / n, y: 0, w: w / n, h },
  );
}

/** SVG transform placing table-local space at its spot in the room. */
export function tableTransform(type: TileType, trestles: number, x: number, y: number, rotation: number): string {
  const s = tableSize(type, trestles);
  const r = rotatedTableSize(type, trestles, rotation);
  return `translate(${x + r.w / 2} ${y + r.h / 2}) rotate(${normaliseRotation(rotation)}) translate(${-s.w / 2} ${-s.h / 2})`;
}

/** Map a room point into a table's local space (inverse of tableTransform). */
export function toLocal(
  type: TileType,
  trestles: number,
  x: number,
  y: number,
  rotation: number,
  px: number,
  py: number,
): { x: number; y: number } {
  const s = tableSize(type, trestles);
  const r = rotatedTableSize(type, trestles, rotation);
  const dx = px - (x + r.w / 2);
  const dy = py - (y + r.h / 2);
  const rad = (-normaliseRotation(rotation) * Math.PI) / 180;
  return {
    x: dx * Math.cos(rad) - dy * Math.sin(rad) + s.w / 2,
    y: dx * Math.sin(rad) + dy * Math.cos(rad) + s.h / 2,
  };
}

/** Chair counts per side from a table's seats. */
export function sideCountsOf(seats: ReadonlyArray<{ side?: Side | null }>): SideCounts {
  const counts = emptyCounts();
  for (const seat of seats) {
    counts[(seat.side ?? 'top') as Side]++;
  }
  return counts;
}

export function snapToGrid(value: number): number {
  return Math.round(value / SNAP_STEP) * SNAP_STEP;
}

export function clampToCanvas(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

/**
 * Footprint (table + default chairs) of one piece at rotation 0, for the
 * retired TVG-144 layout generator and the Furniture panel thumbnails.
 */
export const TILE_DIMENSIONS: Record<TileType, Size> = Object.fromEntries(
  (['long_12', 'single_6', 'single_3', 'single_3_below', 'single_3_vertical'] as TileType[]).map((t) => {
    const b = footprint(t, 1, 0, 0, 0, defaultSideCounts(t, 1, DEFAULT_FURNITURE.seatsPerSide));
    return [t, { w: b.x2 - b.x1, h: b.y2 - b.y1 }];
  }),
) as Record<TileType, Size>;

/** Default seat total of one piece at the default seats per side. */
export const TILE_SEAT_COUNT: Record<TileType, number> = Object.fromEntries(
  (['long_12', 'single_6', 'single_3', 'single_3_below', 'single_3_vertical'] as TileType[]).map((t) => {
    const c = defaultSideCounts(t, 1, DEFAULT_FURNITURE.seatsPerSide);
    return [t, c.top + c.right + c.bottom + c.left];
  }),
) as Record<TileType, number>;

/** A saved-template table, as drawn in thumbnails and the ghost preview. */
export interface PreviewTableSource {
  tile_type: TileType;
  trestle_count?: number;
  sides?: Partial<SideCounts> | null;
  seat_count: number;
  position_x: number;
  position_y: number;
  rotation: number;
}

/**
 * Everything needed to draw a saved-template table: its transform, trestles
 * and chairs (local space). Older templates without sides spread their seat
 * total over the seated long sides.
 */
export function previewPiece(t: PreviewTableSource): {
  transform: string;
  trestles: Array<{ x: number; y: number; w: number; h: number }>;
  chairs: Array<{ x: number; y: number; w: number; h: number }>;
} {
  const n = trestleCount(t.tile_type, t.trestle_count ?? 1);
  const counts: SideCounts = t.sides
    ? { ...emptyCounts(), ...t.sides }
    : distributeSeatCount(t.tile_type, t.seat_count);
  const chairs: Array<{ x: number; y: number; w: number; h: number }> = [];
  for (const side of SIDES) {
    for (let i = 0; i < counts[side]; i++) {
      chairs.push(chairRect(t.tile_type, n, side, i, counts[side]));
    }
  }
  return {
    transform: tableTransform(t.tile_type, n, t.position_x, t.position_y, t.rotation),
    trestles: trestleRects(t.tile_type, n),
    chairs,
  };
}
